import { defineMessages } from 'react-intl';

import { schemaExtender } from './itemSchemaExtender';

const messages = defineMessages({
  Target: {
    id: 'Item link',
    defaultMessage: 'Item link',
  },
  TargetDescription: {
    id: 'Enter URL or choose content from portal',
    defaultMessage: 'Enter URL or choose content from portal',
  },
  title: {
    id: 'Title',
    defaultMessage: 'Title',
  },
  description: {
    id: 'Description',
    defaultMessage: 'Description',
  },
  image: {
    id: 'Image',
    defaultMessage: 'Image',
  },
  imageHint: {
    id: 'Upload or drag an image, or browse and select one from the site',
    defaultMessage:
      'Upload or drag an image, or browse and select one from the site',
  },
  item: {
    id: 'Item',
    defaultMessage: 'Item',
  },
  items: {
    id: 'Items',
    defaultMessage: 'Items',
  },
  addItem: {
    id: 'Add item',
    defaultMessage: 'Add item',
  },
  phone: {
    id: 'Phone',
    defaultMessage: 'Phone',
  },
  tablet: {
    id: 'Tablet',
    defaultMessage: 'Tablet',
  },
  desktop: {
    id: 'Desktop',
    defaultMessage: 'Desktop',
  },
  content: {
    id: 'Content',
    defaultMessage: 'Default',
  },
  hideInfo: {
    id: 'Hide Title and Description',
    defaultMessage: 'Hide Title and Description',
  },
  hideInfoHint: {
    id: 'If selected, the Title and Description fields will be hidden in the carousel item view.',
    defaultMessage:
      'If selected, the Title and Description fields will be hidden in the carousel item view.',
  },
  layout: {
    id: 'Layout',
    defaultMessage: 'Layout',
  },
  textBelow: {
    id: 'Text Below',
    defaultMessage: 'Text Below',
  },
  textInner: {
    id: 'Text Inner',
    defaultMessage: 'Text Inner',
  },
  imageAspectRatio: {
    id: 'Image aspect ratio',
    defaultMessage: 'Image aspect ratio',
  },
  aspectRatioMode: {
    id: 'Aspect ratio mode',
    defaultMessage: 'Image adjustment',
  },
  aspectRatioFixed: {
    id: 'Fixed',
    defaultMessage: 'Fixed',
  },
  aspectRatioFree: {
    id: 'Free',
    defaultMessage: 'Free (automatic height)',
  },
  aspectRatioModeHelp: {
    id: 'Aspect ratio mode help',
    defaultMessage:
      'Fixed uses the selected aspect ratios by device. Free uses the natural image proportions.',
  },
  aspectRatioDesktop: {
    id: 'Aspect ratio - Desktop',
    defaultMessage: 'Aspect ratio - Desktop',
  },
  aspectRatioTablet: {
    id: 'Aspect ratio - Tablet',
    defaultMessage: 'Aspect ratio - Tablet',
  },
  aspectRatioPhone: {
    id: 'Aspect ratio - Phone',
    defaultMessage: 'Aspect ratio - Phone',
  },
  infinite: {
    id: 'Infinite',
    defaultMessage: 'Infinite',
  },
  infiniteDesc: {
    id: 'Enable carousel navigation between the first and last items.',
    defaultMessage:
      'Enable carousel navigation between the first and last items.',
  },
  autoplay: {
    id: 'Autoplay',
    defaultMessage: 'Autoplay',
  },
  autoplaySpeed: {
    id: 'Speed',
    defaultMessage: 'Speed',
  },
  autoplaySpeedDesc: {
    id: 'Time between items, in seconds',
    defaultMessage: 'Time between items, in seconds',
  },
  settings: {
    id: 'Settings',
    defaultMessage: 'Settings',
  },
  Carousel: {
    id: 'Carousel',
    defaultMessage: 'Carousel',
  },
  overwrite: {
    id: 'Customize content',
    defaultMessage: 'Customize content',
  },
  overwriteDescription: {
    id: 'The item will update when the target is edited. Check this option to keep the content fixed and customize.',
    defaultMessage:
      'The item will update when the target is edited. Check this option to keep the content fixed and customize.',
  },
  alignmentDescription: {
    id: 'Alignment description',
    defaultMessage:
      'The image can be repositioned if an important part is cropped. Depending on the image format, only one of the two fields will take effect.',
  },
  vertical_align: {
    id: 'Vertical align',
    defaultMessage: 'Vertical align',
  },
  horizontal_align: {
    id: 'Horizontal align',
    defaultMessage: 'Horizontal align',
  },
  AltText: {
    id: 'Image description (alt text)',
    defaultMessage: 'Image description (alt text)',
  },
  AltTextHint: {
    id: 'Leave empty if the image is purely decorative.',
    defaultMessage: 'Leave empty if the image is purely decorative.',
  },
  AltTextHintLinkText: {
    id: 'Describe the purpose of the image.',
    defaultMessage: 'Describe the purpose of the image.',
  },
  openLinkInNewTab: {
    id: 'Open in a new tab',
    defaultMessage: 'Open in a new tab',
  },
  customizedDescription: {
    id: 'Customized Description',
    defaultMessage:
      'The values filled in for a device will be used for the lower devices (desktop → tablet → phone) if the corresponding fields are empty.',
  },
  withoutSource: {
    id: 'Without source',
    defaultMessage: 'Without source',
  },
  withoutSourceDescription: {
    id: 'Use this option to create an image gallery without navigation links. Ideal for displaying images for viewing only.',
    defaultMessage:
      'Use this option to create an image gallery without navigation links. Ideal for displaying images for viewing only.',
  },
});

const generateItemSchema = (device, intl) => ({
  [`Title_${device}`]: {
    title: intl.formatMessage(messages.title),
  },
  [`Description_${device}`]: {
    title: intl.formatMessage(messages.description),
    widget: 'textarea',
  },
  [`preview_image_${device}`]: {
    title: intl.formatMessage(messages.image),
    widget: 'image',
    hideLinkPicker: true,
    description: intl.formatMessage(messages.imageHint),
    factory: 'Relation Choice',
  },
  [`alt_${device}`]: {
    title: intl.formatMessage(messages.AltText),
    type: 'string',
    description: intl.formatMessage(messages.AltTextHintLinkText),
  },
  [`alignment_help_${device}`]: {
    widget: 'help_message_widget',
    body: intl.formatMessage(messages.alignmentDescription),
  },
  [`vertical_alignment_${device}`]: {
    title: intl.formatMessage(messages.vertical_align),
    type: 'string',
    widget: 'position_widget',
    direction: 'vertical',
  },
  [`horizontal_alignment_${device}`]: {
    title: intl.formatMessage(messages.horizontal_align),
    type: 'string',
    widget: 'position_widget',
    direction: 'horizontal',
  },
});

const generateFieldset = (device, intl) => ({
  id: device,
  title: intl.formatMessage(messages[device]),
  fields: [
    `Title_${device}`,
    `Description_${device}`,
    `preview_image_${device}`,
    `alt_${device}`,
    `alignment_help_${device}`,
    `horizontal_alignment_${device}`,
    `vertical_alignment_${device}`,
  ],
});

const itemSchema = (props) => {
  const { intl } = props;

  const devices = ['desktop', 'tablet', 'phone'];
  const properties = devices.reduce(
    (acc, device) => ({ ...acc, ...generateItemSchema(device, intl) }),
    {},
  );
  const fieldsets = devices.map((device) => generateFieldset(device, intl));

  const customizedSchema = {
    title: intl.formatMessage(messages.item),
    fieldsets,
    properties,
    required: [],
  };

  return {
    title: intl.formatMessage(messages.item),
    addMessage: intl.formatMessage(messages.addItem),
    fieldsets: [
      {
        id: 'content',
        title: intl.formatMessage(messages.content),
        fields: ['href', 'withoutSource'],
      },
    ],
    properties: {
      href: {
        title: intl.formatMessage(messages.Target),
        description: intl.formatMessage(messages.TargetDescription),
        widget: 'object_browser',
        mode: 'link',
        selectedItemAttrs: [
          'Title',
          'Description',
          'image_field',
          'review_state',
          'alt',
        ],
        allowExternals: true,
      },
      withoutSource: {
        title: intl.formatMessage(messages.withoutSource),
        description: intl.formatMessage(messages.withoutSourceDescription),
        type: 'boolean',
        default: false,
      },
      hide_info: {
        title: props.intl.formatMessage(messages.hideInfo),
        description: props.intl.formatMessage(messages.hideInfoHint),
        type: 'boolean',
      },
      alignment_help: {
        widget: 'help_message_widget',
        body: intl.formatMessage(messages.alignmentDescription),
      },
      horizontal_alignment: {
        title: intl.formatMessage(messages.horizontal_align),
        type: 'string',
        widget: 'position_widget',
        direction: 'horizontal',
        default: 'center',
      },
      vertical_alignment: {
        title: intl.formatMessage(messages.vertical_align),
        type: 'string',
        widget: 'position_widget',
        direction: 'vertical',
        default: 'center',
      },
      overwrite: {
        title: intl.formatMessage(messages.overwrite),
        type: 'boolean',
        default: false,
      },
      overwrite_help: {
        widget: 'help_message_widget',
        body: intl.formatMessage(messages.overwriteDescription),
      },
      customized_help: {
        widget: 'help_message_widget',
        body: intl.formatMessage(messages.customizedDescription),
      },
      customized: {
        title: 'customized_devices',
        widget: 'object',
        schema: customizedSchema,
      },
    },
    required: [],
  };
};

export const carouselSchema = (props) => {
  const { intl, data = {} } = props;

  const aspectRatioChoices = [
    ['100%', '1/1'],
    ['66.6666%', '3/2'],
    ['75%', '4/3'],
    ['56.25%', '16/9'],
    ['50%', '2/1'],
    ['33.3333%', '3/1'],
    ['125%', '4/5'],
    ['120%', '5/6'],
    ['150%', '3/4'],
    ['177.78%', '9/16'],
  ];

  return {
    title: props.intl.formatMessage(messages.Carousel),
    block: 'carousel',
    fieldsets: [
      {
        id: 'default',
        title: intl.formatMessage(messages.content),
        fields: ['columns'],
      },
      {
        id: 'aspectRatio',
        title: intl.formatMessage(messages.imageAspectRatio),
        fields: [
          'aspectRatioMode',
          ...(data?.aspectRatioMode !== 'free'
            ? [
                'aspectRatioHelp',
                'aspectRatioDesktop',
                'aspectRatioTablet',
                'aspectRatioPhone',
              ]
            : []),
        ],
      },
      {
        id: 'settings',
        title: props.intl.formatMessage(messages.settings),
        fields: ['layout', 'infinite', 'autoplay', 'autoplaySpeed'],
      },
    ],
    properties: {
      columns: {
        widget: 'object_list',
        title: props.intl.formatMessage(messages.items),
        schema: itemSchema(props),
        schemaExtender: schemaExtender,
      },
      infinite: {
        title: intl.formatMessage(messages.infinite),
        description: intl.formatMessage(messages.infiniteDesc),
        type: 'boolean',
      },
      autoplay: {
        title: intl.formatMessage(messages.autoplay),
        type: 'boolean',
      },
      autoplaySpeed: {
        title: intl.formatMessage(messages.autoplaySpeed),
        description: intl.formatMessage(messages.autoplaySpeedDesc),
        type: 'number',
        default: 6,
        isDisabled: data?.autoplay === false,
      },
      layout: {
        title: intl.formatMessage(messages.layout),
        required: true,
        noValueOption: false,
        default: 'c-default',
        type: 'string',
        factory: 'Choice',
        choices: [
          ['c-default', intl.formatMessage(messages.textBelow)],
          ['back', intl.formatMessage(messages.textInner)],
        ],
      },
      aspectRatioHelp: {
        widget: 'help_message_widget',

        body: 'Select the image format for each device',
      },
      aspectRatioMode: {
        title: intl.formatMessage(messages.aspectRatioMode),
        description: intl.formatMessage(messages.aspectRatioModeHelp),
        type: 'string',
        widget: 'radio_group',
        default: 'fixed',
        choices: [
          ['fixed', intl.formatMessage(messages.aspectRatioFixed)],
          ['free', intl.formatMessage(messages.aspectRatioFree)],
        ],
      },
      aspectRatioDesktop: {
        title: intl.formatMessage(messages.desktop),
        required: true,
        noValueOption: false,
        default: '56.25%',
        type: 'string',
        factory: 'Choice',
        choices: aspectRatioChoices,
      },
      aspectRatioTablet: {
        title: intl.formatMessage(messages.tablet),
        required: true,
        noValueOption: false,
        default: '75%',
        type: 'string',
        factory: 'Choice',
        choices: aspectRatioChoices,
      },
      aspectRatioPhone: {
        title: intl.formatMessage(messages.phone),
        required: true,
        noValueOption: false,
        default: '66.6666%',
        type: 'string',
        factory: 'Choice',
        choices: aspectRatioChoices,
      },
    },
    required: [],
  };
};
